import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { CloseCircle } from 'iconsax-react';
import { Fragment, forwardRef, type ReactNode, type RefObject, useImperativeHandle, useRef, useState } from 'react';
import { useEventListener } from 'usehooks-ts';
import cn from '@/utils/merge-class-names';

export interface ModalProps {
  children: ReactNode;
  clickOutsideToClose?: boolean;
  className?: string;
  contentClassName?: string;
  fullScreen?: boolean;
  backdrop?: boolean;
  initialFocus?: RefObject<HTMLElement>;
  defaultOpen?: boolean;
  onClose?: () => void;
  bottomCloseButton?: boolean;
  background?: 'black' | 'transparent';
}

export interface ModalAction {
  open: () => void;
  close: () => void;
}

const Modal = forwardRef<ModalAction, ModalProps>(
  (
    {
      children,
      clickOutsideToClose,
      className,
      contentClassName,
      initialFocus,
      onClose,
      fullScreen = false,
      backdrop = true,
      defaultOpen = false,
      background = 'black',
      bottomCloseButton = false,
    },
    ref,
  ) => {
    const dialogRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(defaultOpen);

    const handleOutsideClick = () => {
      if (!clickOutsideToClose) return;

      setIsOpen(false);
      onClose?.();
    };

    useEventListener(
      'contextmenu',
      (e) => {
        if (dialogRef.current) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      dialogRef,
    );

    useImperativeHandle(ref, () => ({
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
    }));

    return (
      <Transition
        appear
        show={isOpen}
        as={Fragment}>
        <Dialog
          static
          as='div'
          ref={dialogRef}
          className='relative z-50'
          onClose={handleOutsideClick}
          initialFocus={initialFocus}>
          {backdrop && (
            <TransitionChild
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'>
              <div
                className={cn('fixed inset-0 pointer-events-none', {
                  'bg-transparent': background === 'transparent',
                  'bg-black/75 backdrop-blur-sm': background === 'black',
                })}
              />
            </TransitionChild>
          )}

          <div className='fixed inset-0 overflow-y-auto'>
            <div
              className={cn(
                'flex min-h-full flex-col items-center justify-center px-4 py-safe-offset-4 text-center transition-all',
                { 'p-0': fullScreen },
                className,
              )}>
              <TransitionChild
                as={Fragment}
                enter='ease-out duration-300'
                enterFrom='opacity-0 translate-y-1/4 scale-95'
                enterTo='opacity-100 scale-100 translate-y-0'
                leave='ease-in duration-200'
                leaveFrom='opacity-100 scale-100 translate-y-0'
                leaveTo='opacity-0 scale-95 -translate-y-1/4'>
                <DialogPanel
                  className={cn(
                    'w-[20rem] max-w-screen-xxs desktop:max-w-screen-lg overflow-hidden rounded-2xl bg-neutral-0 px-6 py-2 text-left align-middle shadow-modal transition-all',
                    { 'p-0 max-w-none rounded-none': fullScreen },
                    contentClassName,
                  )}>
                  {children}
                </DialogPanel>
              </TransitionChild>
              {bottomCloseButton && (
                <button
                  type='button'
                  className='btn btn-ghost btn-circle btn-md mt-4'
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(false);
                    onClose?.();
                  }}>
                  <CloseCircle
                    variant='Bold'
                    className='text-neutral-0 size-10'
                  />
                </button>
              )}
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  },
);

Modal.displayName = 'Modal';

export default Modal;
